import {
  resetViewAccessGroup,
  resetWriteAccessGroup,
  setViewAccessGroup,
  setWriteAccessGroup,
} from '@Reducers/AccessGroup';
import { AppDispatch, RootStateType } from '@Store';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { Avatar, Box, Collapse, Grid, IconButton, Switch, Tooltip, Typography } from '@mui/material';

import { alpha, styled } from '@mui/material/styles';

import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Actions } from './Actions';

import { checkAllowedActions } from '@Reducers/AccessGroup/case';
import { ActionIdsType, PagesDataEntity } from '@Reducers/AccessGroup/Type';

import {
  SettingsOutlined,
  WarehouseOutlined,
  LocalShippingOutlined,
  VerifiedUserOutlined,
  ReceiptLongOutlined,
  BusinessOutlined,
  FlightTakeoffOutlined,
  NotificationsActiveOutlined,
  SchoolOutlined,
  SecurityOutlined,
  PeopleAltOutlined,
  AdminPanelSettingsOutlined,
  DraftsOutlined,
  ViewListOutlined,
  HistoryOutlined,
  AssessmentOutlined,
  AccountBalanceWalletOutlined,
  GroupWorkOutlined,
  TaskAltOutlined,
  AltRouteOutlined,
  PriceCheckOutlined,
  HubOutlined,
  MonetizationOnOutlined,
  ReceiptOutlined,
} from '@mui/icons-material';

const MODULE_ICON_MAP: Record<string, React.ReactNode> = {
  // System
  System: <SettingsOutlined />,
  Warehouse: <WarehouseOutlined />,
  'Carrier & Services': <LocalShippingOutlined />,
  KYC: <VerifiedUserOutlined />,
  Invoice: <ReceiptLongOutlined />,
  Organization: <BusinessOutlined />,
  Shipment: <FlightTakeoffOutlined />,
  'Notification Settings': <NotificationsActiveOutlined />,
  'Knowledge Center': <SchoolOutlined />,
  'Access Groups': <SecurityOutlined />,

  // Users / Admin
  User: <PeopleAltOutlined />,
  Admin: <AdminPanelSettingsOutlined />,

  // Shipments
  'Draft Shipments': <DraftsOutlined />,
  Manifest: <ViewListOutlined />,
  'Shipment reports': <AssessmentOutlined />,
  'Shipment History': <HistoryOutlined />,

  // Pricing / Contracts
  Base: <PriceCheckOutlined />,
  Tariff: <PriceCheckOutlined />,
  Contract: <BusinessOutlined />,
  'Global Charges': <MonetizationOnOutlined />,
  ODA: <LocalShippingOutlined />,
  'Pick up Pricing': <PriceCheckOutlined />,

  // Finance
  'View Invoices - Account holder': <ReceiptOutlined />,
  'View Invoices - Non-Account holder': <ReceiptOutlined />,
  Transactions: <AccountBalanceWalletOutlined />,
  'Credits & Wallet': <AccountBalanceWalletOutlined />,
  Invoices: <ReceiptLongOutlined />,
  'Credit and Transactions': <AccountBalanceWalletOutlined />,

  // Operations
  'Squad Management': <GroupWorkOutlined />,
  'Task Management': <TaskAltOutlined />,
  'Route Optimizer': <AltRouteOutlined />,
  'Squad Reports': <AssessmentOutlined />,

  // Misc
  'Get Rates': <PriceCheckOutlined />,
  Profile: <VerifiedUserOutlined />,
  'Hub App Access': <HubOutlined />,
};

const getModuleIcon = (name: string) => {
  return MODULE_ICON_MAP[name] || <SettingsOutlined />;
};

export function Pages({ moduleName, data }: { moduleName: string; data: PagesDataEntity[] }) {
  return (
    <Grid container item className="mt-3">
      <Box width="100%">
        {/* Header */}
        <Box mb={3}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {moduleName?.toUpperCase()}
          </Typography>

          <Typography
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              fontSize: '14px',
            }}
          >
            Manage user permissions and access levels
          </Typography>
        </Box>

        {/* Container */}
        <Box
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.12)}`,

            background: theme => alpha(theme.palette.background.paper, 0.75),

            backdropFilter: 'blur(14px)',
          }}
        >
          {/* Header Row */}
          <HeaderRow>
            <InfoSection>
              <Typography fontWeight={700}>Module / Feature</Typography>
            </InfoSection>

            <PermissionCol>
              <Typography fontWeight={700} color="primary.main">
                View Access
              </Typography>
            </PermissionCol>

            <PermissionCol>
              <Typography fontWeight={700} color="primary.main">
                Edit Access
              </Typography>
            </PermissionCol>
          </HeaderRow>

          {/* Rows */}
          {data?.map((item: PagesDataEntity) => {
            const hasActions = Boolean(item?.actionIds?.length);

            return (
              <Row
                key={item._id}
                name={item.name}
                pageId={item._id}
                actionIdsArr={item.actionIds ?? []}
                hasActions={hasActions}
              />
            );
          })}
        </Box>
      </Box>
    </Grid>
  );
}

function Row({ name, pageId, actionIdsArr, hasActions }: RowProps) {
  const dispatch = useDispatch<AppDispatch>();

  const { actionsList, actionsIds, pageIds } = useSelector((state: RootStateType) => state.accessGroup);

  const [isCollapse, setIsCollapse] = useState<boolean>(false);

  const actionsByPage = useMemo(() => {
    if (actionIdsArr && actionIdsArr?.length) {
      return actionIdsArr?.map(id => actionsList[id]);
    }

    return [];
  }, [actionsList, actionIdsArr]);

  // Checking edit access for all actions for page
  const isAllEditTrue = useMemo(() => {
    return (
      actionIdsArr?.length > 0 &&
      actionsIds?.length > 0 &&
      actionsIds?.filter((item: ActionIdsType) => {
        const { isWriteOnly } = checkAllowedActions(item?.selectedActions);

        return isWriteOnly && JSON.stringify(actionIdsArr)?.includes(item._id);
      })?.length ===
        actionsByPage?.filter(data => {
          return data?.isWriteOnly;
        })?.length
    );
  }, [actionsByPage, actionIdsArr, actionsIds]);

  // Checking view access for all actions for page
  const isAllViewTrue = useMemo(() => {
    return JSON.stringify(pageIds)?.includes(pageId);
  }, [pageIds, pageId]);

  const handleViewCheckBoxChange = () => {
    if (!isAllViewTrue) {
      dispatch(
        setViewAccessGroup({
          pageId: pageId,
          actionIds: actionIdsArr,
        }),
      );
    } else {
      dispatch(
        resetViewAccessGroup({
          pageId: pageId,
          actionIds: actionIdsArr,
        }),
      );
    }
  };

  const handleEditCheckBoxChange = () => {
    if (!isAllEditTrue) {
      if (actionsIds?.length !== 0) {
        dispatch(
          resetWriteAccessGroup({
            actionIds: actionIdsArr,
          }),
        );
      }

      dispatch(
        setWriteAccessGroup({
          actionIds: actionIdsArr,
        }),
      );
    } else {
      dispatch(
        resetWriteAccessGroup({
          actionIds: actionIdsArr,
        }),
      );
    }
  };

  return (
    <Box>
      {/* Parent Row */}
      <StyledRow>
        <InfoSection
          onClick={() => {
            if (actionIdsArr.length > 0) {
              setIsCollapse(!isCollapse);
            }
          }}
          sx={{
            cursor: actionIdsArr.length > 0 ? 'pointer' : 'default',
          }}
        >
          <Tooltip title={isCollapse ? 'Collapse' : 'Expand'}>
            {actionIdsArr?.length > 0 ? (
              <IconButton
                size="small"
                onClick={() => setIsCollapse(!isCollapse)}
                sx={{
                  opacity: 0.8,
                }}
              >
                {isCollapse ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </IconButton>
            ) : (
              <Box
                sx={{
                  width: 40,
                }}
              />
            )}
          </Tooltip>

          <AvatarBox>{getModuleIcon(name)}</AvatarBox>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '15px',
              color: 'text.primary',
            }}
          >
            {name}
          </Typography>
        </InfoSection>

        {/* View */}
        <PermissionCol>
          <StyledSwitch checked={isAllViewTrue} onChange={handleViewCheckBoxChange} />
        </PermissionCol>

        {/* Edit */}
        <PermissionCol>
          <StyledSwitch
            disabled={!isAllViewTrue}
            checked={isAllEditTrue}
            onChange={handleEditCheckBoxChange}
            sx={{
              cursor: hasActions ? 'pointer' : 'not-allowed',
            }}
          />
        </PermissionCol>
      </StyledRow>

      {/* Children */}
      <Collapse in={isCollapse} timeout="auto" unmountOnExit>
        {actionsByPage?.map(actionData => (
          <Actions key={actionData?._id} isAllViewTrue={isAllViewTrue} data={actionData} pageId={pageId} />
        ))}
      </Collapse>
    </Box>
  );
}

/* -------------------- STYLES -------------------- */

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '18px 24px',

  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,

  background: alpha(theme.palette.primary.main, 0.04),
}));

const StyledRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  padding: '16px 24px',

  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,

  transition: 'all 0.25s ease',

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const InfoSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: 1,
});

const PermissionCol = styled(Box)({
  width: 120,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const AvatarBox = styled(Avatar)(({ theme }) => ({
  width: 38,
  height: 38,

  background: alpha(theme.palette.secondary.main, 0.1),

  color: theme.palette.primary.main,
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },

  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.primary.light,
    opacity: 1,
  },

  '& .MuiSwitch-track': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.3),
  },

  '& .MuiSwitch-switchBase': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

interface RowProps {
  name: string;
  pageId: string;
  actionIdsArr: string[] | [];
  hasActions: boolean;
}
